import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
// Pages and components
import Header from "./component/common/header";
import Home from "./pages/home/index";
import ProjectDisplay from "./pages/projectDisplay/index";
import NoPageFound from "./pages/noPageFound/index";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import AboutUS from "./pages/aboutUs";
import Portfolio from "./pages/portfolio";
import Pricing from "./pages/price";
import BlockchainPage from "./pages/blockchain";
import CloudPage from "./pages/cloud";
import MobileDevelopmentPage from "./pages/mobileDevelopment";
import WebPage from "./pages/web";
import DesigningPage from "./pages/designing";
import PrivacyPolicy from "./pages/privacy";
// Analytics
import { grantConsent } from "./analytics";
import ChangeTracker from "./component/util/ChangeTracker";
import CookieConsentBanner from "./component/common/cookieConsentBanner";
import { getHtmlLang } from "./i18n";
// Styles
import "./rtl.css";
import Footer from "./component/common/footer";

function App() {
  const { i18n } = useTranslation();
  const currentLang = getHtmlLang(i18n.language);

  // Sync HTML lang attribute with i18n language changes
  useEffect(() => {
    document.documentElement.lang = getHtmlLang(i18n.language);
  }, [i18n.language]);

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": "https://www.bringitgo.tech/organization",
      name: "BringIt Go",
      alternateName: [
        "bringitgo",
        "bring it go",
        "bringitgo tech",
        "BringItGo Tech",
      ],
      url: "https://www.bringitgo.tech/",
      logo: "https://cdn.bringitgo.tech/big%20logo.png",
      description:
        "BringIt Go is a technology company offering web services including web development, UI/UX design, SaaS application development, cloud infrastructure solutions, and blockchain application services.",
      founder: {
        "@type": "Person",
        name: "Mubashar Hussain",
      },
      foundingDate: "2023",
      sameAs: [
        "https://www.facebook.com/Bringitgotech/",
        "https://github.com/bringitgo",
      ],
      contactPoint: {
        "@type": "ContactPoint",
        telephone: "+44-7413-402742",
        contactType: "Customer Service",
        email: "info@bringitgo.tech",
        areaServed: "GB",
        availableLanguage: ["English", "Arabic", "German", "French"],
      },
      address: {
        "@type": "PostalAddress",
        streetAddress: "Curlew Way",
        addressLocality: "Inverkeithing",
        postalCode: "KY11 1FG",
        addressCountry: "GB",
      },
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Web Services",
        itemListElement: [
          {
            "@type": "Offer",
            "@id": "https://www.bringitgo.tech/web",
            itemOffered: {
              "@type": "Service",
              name: "Web Development",
              description: "Custom websites and full-stack web applications.",
            },
          },
          {
            "@type": "Offer",
            "@id": "https://www.bringitgo.tech/designing",
            itemOffered: {
              "@type": "Service",
              name: "Web Design",
              description:
                "UI/UX design services for modern, responsive websites.",
            },
          },
          {
            "@type": "Offer",
            "@id": "https://www.bringitgo.tech/mobile-development",
            itemOffered: {
              "@type": "Service",
              name: "Mobile App Development",
              description:
                "Native and cross-platform mobile application development.",
            },
          },
          {
            "@type": "Offer",
            "@id": "https://www.bringitgo.tech/cloud",
            itemOffered: {
              "@type": "Service",
              name: "Cloud Infrastructure",
              description: "Cloud infrastructure setup and DevOps support.",
            },
          },
          {
            "@type": "Offer",
            "@id": "https://www.bringitgo.tech/blockchain",
            itemOffered: {
              "@type": "Service",
              name: "Blockchain Development",
              description:
                "Decentralized applications and blockchain integration services.",
            },
          },
        ],
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": "https://www.bringitgo.tech/website",
      url: "https://www.bringitgo.tech/",
      name: "BringIt Go",
      inLanguage: currentLang,
      description:
        "Technology company offering web development, UI/UX design, cloud infrastructure, and blockchain services. Best website builder for small business",
      publisher: {
        "@id": "https://www.bringitgo.tech/organization",
      },
    },
  ];
  return (
    <>
      <Helmet>
        <html lang={currentLang} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      <Router>
        <Header />
        <ChangeTracker />
        <Routes>
          <Route exact path="/" element={<Home />} />
          <Route exact path="/project/:id" element={<ProjectDisplay />} />
          <Route exact path="/about-us" element={<AboutUS />} />
          <Route exact path="/blockchain" element={<BlockchainPage />} />
          <Route exact path="/cloud" element={<CloudPage />} />
          <Route exact path="/designing" element={<DesigningPage />} />
          <Route
            exact
            path="/mobile-development"
            element={<MobileDevelopmentPage />}
          />
          <Route exact path="/portfolio" element={<Portfolio />} />
          <Route exact path="/web" element={<WebPage />} />
          <Route exact path="/pricing" element={<Pricing />} />
          <Route exact path="/privacy" element={<PrivacyPolicy />} />
          <Route path="*" element={<NoPageFound />} />
        </Routes>
      </Router>
      <Footer />
      <CookieConsentBanner />
    </>
  );
}

export default App;
